/**
 * Utility functions for CSV export functionality
 */

export interface CSVExportOptions {
  filename: string;
  headers: string[];
  data: any[];
  delimiter?: string;
}

/**
 * Converts data to CSV format and triggers download
 */
export function exportToCSV(options: CSVExportOptions): void {
  const { filename, headers, data, delimiter = ',' } = options;
  
  // Create CSV content
  const csvContent = [
    // Headers
    headers.join(delimiter),
    // Data rows
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape values that contain delimiter, quotes, or newlines
        if (typeof value === 'string' && (value.includes(delimiter) || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(delimiter)
    )
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Formats date for CSV export
 */
export function formatDateForCSV(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Exports users to CSV
 */
export function exportUsersToCSV(users: any[]): void {
  const headers = ['ID', 'Username', 'Email', 'Role', 'Status', 'Created At'];
  const data = users.map(user => ({
    'ID': user.id,
    'Username': user.username,
    'Email': user.email,
    'Role': user.role,
    'Status': user.enabled ? 'Active' : 'Inactive',
    'Created At': formatDateForCSV(user.createdAt)
  }));

  exportToCSV({
    filename: `users-export-${new Date().toISOString().split('T')[0]}`,
    headers,
    data
  });
}

/**
 * Exports email subscribers to CSV
 */
export function exportSubscribersToCSV(subscribers: any[]): void {
  const headers = ['ID', 'Email', 'Status', 'Subscribed At'];
  const data = subscribers.map(subscriber => ({
    'ID': subscriber.id,
    'Email': subscriber.email,
    'Status': subscriber.active ? 'Active' : 'Inactive',
    'Subscribed At': formatDateForCSV(subscriber.subscribedAt)
  }));

  exportToCSV({
    filename: `subscribers-export-${new Date().toISOString().split('T')[0]}`,
    headers,
    data
  });
}
